import { useCallback, useRef, useState } from 'react';

// 슬롯끼리 끌어서 자리를 바꾼다. 지금까지는 눌러서 넣는 것만 되고, 이미 넣은 니케의
// 순서를 고치려면 둘 다 지우고 다시 넣어야 했다.
//
// HTML5 드래그(draggable)는 터치에서 동작하지 않는다. 폰에서 쓰는 화면이므로 포인터
// 이벤트로 직접 만든다. 짧게 누른 것은 지금처럼 선택창을 여는 탭으로 남아야 하니,
// 움직인 거리로 가른다 - 이벤트 개수로 세면 크게 끌어도 두세 번이라 탭으로 읽힌다.
const TAP_SLACK = 8;

function slotIndexAt(group, x, y) {
  if (typeof document === 'undefined') return -1;
  const node = document.elementFromPoint(x, y);
  const slot = node?.closest?.(`[data-slot-group="${group}"]`);
  if (!slot) return -1;
  const index = Number(slot.getAttribute('data-slot-index'));
  return Number.isInteger(index) ? index : -1;
}

/**
 * group 으로 슬롯 줄을 구분한다. 한 화면에 줄이 둘(바깥 덱·선택창 안 스쿼드) 있어서,
 * 한쪽에서 끌던 것이 다른 쪽에 떨어지면 안 된다.
 */
export function useDeckSlotDragSwap(group, onSwap) {
  const dragRef = useRef(null);
  const draggedRef = useRef(false);
  const [from, setFrom] = useState(-1);
  const [over, setOver] = useState(-1);

  const reset = useCallback(() => {
    dragRef.current = null;
    setFrom(-1);
    setOver(-1);
  }, []);

  // 끌고 난 뒤에는 pointerup 에 이어 click 이 한 번 더 온다. 그대로 두면 자리를 바꾸면서
  // 선택창까지 열린다. 바로 다음 click 한 번만 막는다.
  //
  // 표시를 지우는 일은 click 에 맡기지 않는다. 손을 슬롯 밖에서 떼는 등 click 이 따라오지
  // 않는 경우가 있고, 그러면 표시가 남아 다음에 제대로 누른 것까지 삼킨다(측정으로 확인).
  // 다음 pointerdown 에서 지운다 - 새 동작이 시작되면 지난 드래그는 끝난 것이다.
  const shouldIgnoreClick = useCallback(() => draggedRef.current, []);

  const slotProps = useCallback((index) => ({
    'data-slot-group': group,
    'data-slot-index': index,
    onPointerDown: (event) => {
      if (typeof event.button === 'number' && event.button !== 0) return;
      draggedRef.current = false;
      dragRef.current = { index, x: event.clientX, y: event.clientY, moved: 0 };
    },
    onPointerMove: (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      drag.moved = Math.max(
        drag.moved,
        Math.abs(event.clientX - drag.x) + Math.abs(event.clientY - drag.y),
      );
      if (drag.moved < TAP_SLACK) return;
      if (from !== drag.index) setFrom(drag.index);
      const target = slotIndexAt(group, event.clientX, event.clientY);
      setOver(target === drag.index ? -1 : target);
    },
    onPointerUp: (event) => {
      const drag = dragRef.current;
      reset();
      if (!drag || drag.moved < TAP_SLACK) return;
      draggedRef.current = true;
      const target = slotIndexAt(group, event.clientX, event.clientY);
      if (target >= 0 && target !== drag.index) onSwap(drag.index, target);
    },
    onPointerCancel: reset,
    onLostPointerCapture: reset,
  }), [group, from, onSwap, reset]);

  const slotClass = useCallback((index) => {
    if (index === from) return 'dragging';
    if (index === over) return 'drop-target';
    return '';
  }, [from, over]);

  return { slotProps, slotClass, shouldIgnoreClick, dragging: from >= 0 };
}

// 두 자리를 맞바꾼 새 배열. 빈 슬롯과 바꾸면 그냥 옮기는 것이 된다.
export function swapSlots(list, a, b) {
  const next = [...list];
  const held = next[a];
  next[a] = next[b];
  next[b] = held;
  return next;
}
